import { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../context/UserContext';
import { consultarOtorgamientos } from '../api/apiConfig';
import { formatNumberEsCL } from '../utils/numberFormat';

const MAX_RUTS = 2000;

const NIVELES = [
  { value: 'diario', label: 'Otorgamientos diarios' },
  { value: 'semanal', label: 'Otorgamientos semanales' },
  { value: 'mensual', label: 'Otorgamientos mensuales' },
  { value: 'total', label: 'Otorgamientos totales' },
];

/** Normaliza a formato BD: XXXXXXXX-DV (sin puntos). */
const normalizeRutCli = (raw) => {
  if (raw == null) return '';
  let s = String(raw).trim().toUpperCase().replace(/\./g, '').replace(/\s+/g, '');
  if (!s) return '';
  if (s.includes('-')) {
    const [num, dv] = s.split('-');
    const n = String(num || '').replace(/\D/g, '');
    const d = String(dv || '').replace(/[^0-9K]/g, '');
    if (!n || !d) return '';
    return `${n}-${d}`;
  }
  // Sin guión: último carácter = DV
  const clean = s.replace(/[^0-9K]/g, '');
  if (clean.length < 2) return '';
  return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
};

const parseRutsFromText = (text) => {
  const parts = String(text || '')
    .split(/[\n,;\t]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  for (const p of parts) {
    const n = normalizeRutCli(p);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
};

const columnasPorNivel = (nivel) => {
  switch (nivel) {
    case 'diario':
      return ['RUT_CLI', 'RAZON_SOCIAL', 'FECHA', 'MON_ANT'];
    case 'semanal':
      return ['RUT_CLI', 'RAZON_SOCIAL', 'ANIO', 'SEMANA', 'MON_ANT'];
    case 'mensual':
      return ['RUT_CLI', 'RAZON_SOCIAL', 'ANIO', 'MES', 'MON_ANT'];
    case 'total':
    default:
      return ['RUT_CLI', 'RAZON_SOCIAL', 'MON_ANT'];
  }
};

const ConsultaOtorgamientos = () => {
  const { user } = useUser();
  const [rutsText, setRutsText] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [nivel, setNivel] = useState('mensual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const filas = useMemo(() => result?.filas || [], [result]);
  const columnas = useMemo(() => columnasPorNivel(result?.nivel || nivel), [result, nivel]);
  const totalMonAnt = useMemo(() => {
    if (result?.total_mon_ant != null) return Number(result.total_mon_ant) || 0;
    return filas.reduce((acc, row) => acc + (Number(row.MON_ANT) || 0), 0);
  }, [result, filas]);

  const consultar = async (e) => {
    e?.preventDefault?.();
    setError('');
    setResult(null);

    if (!fechaDesde || !fechaHasta) {
      setError('Indica el rango de fechas (desde y hasta).');
      return;
    }
    if (fechaDesde > fechaHasta) {
      setError('La fecha desde no puede ser mayor que la fecha hasta.');
      return;
    }

    try {
      setLoading(true);
      const ruts = parseRutsFromText(rutsText);
      if (!ruts.length) {
        throw new Error('Ingresa al menos un RUT.');
      }
      if (ruts.length > MAX_RUTS) {
        throw new Error(`Máximo ${MAX_RUTS} RUTs por consulta (recibidos: ${ruts.length}).`);
      }

      const resp = await consultarOtorgamientos(user, {
        ruts,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
        nivel,
      });

      // Soporta body stringificado por API Gateway proxy
      let data = resp;
      if (data?.body && typeof data.body === 'string') {
        try {
          data = JSON.parse(data.body);
        } catch {
          /* keep */
        }
      }
      if (data?.error) {
        throw new Error(data.message || data.error);
      }
      if (!data?.filas && !Array.isArray(data)) {
        throw new Error(data?.message || 'Respuesta inesperada del servidor.');
      }
      if (Array.isArray(data)) {
        data = { nivel, filas: data, total_mon_ant: data.reduce((a, r) => a + (Number(r.MON_ANT) || 0), 0) };
      }
      setResult(data);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'No se pudo consultar otorgamientos.');
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = () => {
    if (!filas.length) return;
    const wb = XLSX.utils.book_new();
    const rows = filas.map((row) => {
      const out = {};
      for (const col of columnas) {
        out[col] = row[col] ?? '';
      }
      return out;
    });
    rows.push(
      Object.fromEntries(
        columnas.map((col) => [col, col === 'MON_ANT' ? totalMonAnt : col === 'RUT_CLI' ? 'TOTAL' : '']),
      ),
    );
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Otorgamientos');
    if (result?.ruts_sin_data?.length) {
      const wsSin = XLSX.utils.json_to_sheet(result.ruts_sin_data.map((r) => ({ RUT_CLI: r })));
      XLSX.utils.book_append_sheet(wb, wsSin, 'Sin data');
    }
    XLSX.writeFile(wb, `consulta_otorgamientos_${nivel}_${fechaDesde}_${fechaHasta}.xlsx`);
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Consulta otorgamientos</h1>
      </div>

      <div className="seccion-datos mb-6">
        <p className="text-sm text-gray-600 mb-4">
          Pega una lista de RUTs (formato <code>XXXXXXXX-2</code>), elige el rango de fechas y el nivel
          de agrupación. Se suma solo <strong>MON_ANT</strong>.
        </p>

        <form onSubmit={consultar} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">RUTs (uno por línea, o separados por coma)</label>
            <textarea
              value={rutsText}
              onChange={(e) => setRutsText(e.target.value)}
              rows={6}
              placeholder={'76123456-7\n12345678-5'}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha desde</label>
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fecha hasta</label>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nivel de detalle</label>
              <select
                value={nivel}
                onChange={(e) => setNivel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {NIVELES.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 items-center">
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-50">
              {loading ? 'Consultando...' : 'Consultar'}
            </button>
            <button
              type="button"
              className="btn-primary text-sm disabled:opacity-50"
              disabled={!filas.length}
              onClick={exportarExcel}
            >
              Exportar Excel
            </button>
          </div>
        </form>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
            {error}
          </div>
        )}
      </div>

      {result && (
        <div className="seccion-datos">
          <div className="flex flex-wrap items-baseline gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Resultados</h2>
            <span className="text-sm text-gray-600">
              {filas.length.toLocaleString('es-CL')} filas · Total MON_ANT:{' '}
              <strong>{formatNumberEsCL(totalMonAnt, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
            </span>
            {result.ruts_sin_data?.length > 0 && (
              <span className="text-sm text-amber-700">
                {result.ruts_sin_data.length} RUT(s) sin data en el rango
              </span>
            )}
          </div>

          <div className="tabla-container overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-left">
                  {columnas.map((col) => (
                    <th key={col} className="px-3 py-2 font-semibold text-gray-700 whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.length === 0 ? (
                  <tr>
                    <td colSpan={columnas.length} className="px-3 py-4 text-gray-500">
                      Sin otorgamientos para los RUTs y fechas indicados.
                    </td>
                  </tr>
                ) : (
                  filas.map((row, idx) => (
                    <tr key={idx} className="border-t border-gray-200 hover:bg-slate-50">
                      {columnas.map((col) => (
                        <td
                          key={col}
                          className={`px-3 py-2 ${col === 'RAZON_SOCIAL' ? 'min-w-[220px]' : 'whitespace-nowrap'}`}
                        >
                          {col === 'MON_ANT'
                            ? formatNumberEsCL(Number(row[col]) || 0, {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })
                            : col === 'FECHA' && row[col]
                              ? String(row[col]).slice(0, 10)
                              : row[col] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {filas.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                    {columnas.map((col) => (
                      <td key={col} className="px-3 py-2 whitespace-nowrap">
                        {col === 'MON_ANT'
                          ? formatNumberEsCL(totalMonAnt, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })
                          : col === 'RUT_CLI'
                            ? 'TOTAL'
                            : ''}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultaOtorgamientos;
